// ============================================
// FAQ 留言问答 — Zustand Store
// ============================================

import { create } from 'zustand';
import { supabase } from '../config/supabase';
import type {
  FaqQuestion,
  FaqQuestionDetail,
  FaqQuestionReply,
  FaqQuestionStatus,
  FaqRichTextFormat,
  FaqReplyFaqLink,
  FaqSpace,
} from './types';

interface CreateQuestionInput {
  title: string;
  content: string;
  contentFormat?: FaqRichTextFormat;
  relatedFaqId?: string | null;
}

interface UpdateQuestionInput {
  title: string;
  content: string;
  contentFormat?: FaqRichTextFormat;
}

interface AddReplyInput {
  questionId: string;
  body: string;
  bodyFormat?: FaqRichTextFormat;
  isAdminReply?: boolean;
  linkedFaqIds?: string[];
}

interface UpdateReplyInput {
  questionId: string;
  body: string;
  bodyFormat?: FaqRichTextFormat;
}

interface FaqQuestionState {
  space: FaqSpace | null;
  questions: FaqQuestion[];
  currentQuestion: FaqQuestionDetail | null;
  loading: boolean;
  detailLoading: boolean;
  saving: boolean;
  error: string | null;

  setSpace: (space: FaqSpace | null) => void;
  fetchQuestions: () => Promise<void>;
  fetchQuestionDetail: (id: string) => Promise<FaqQuestionDetail | null>;
  createQuestion: (input: CreateQuestionInput) => Promise<FaqQuestion>;
  updateQuestion: (id: string, input: UpdateQuestionInput) => Promise<FaqQuestion>;
  addReply: (input: AddReplyInput) => Promise<FaqQuestionReply>;
  updateReply: (id: string, input: UpdateReplyInput) => Promise<FaqQuestionReply>;
  updateQuestionStatus: (id: string, status: FaqQuestionStatus) => Promise<void>;
  sendReplyEmail: (replyId: string) => Promise<void>;
  clearCurrentQuestion: () => void;
}

const questionSelect = `
  *,
  author:users!faq_questions_created_by_fkey(id,email,name,avatar_url),
  related_faq:faq_items!faq_questions_related_faq_id_fkey(id,space_id,question,primary_tags,secondary_tags,primary_tag_ids,secondary_tag_ids,is_published,visibility_scope),
  replies:faq_question_replies(id,author_id,is_admin_reply,created_at)
`;

const replySelect = `
  *,
  author:users!faq_question_replies_author_id_fkey(id,email,name,avatar_url)
`;

function normalizeQuestion(row: any): FaqQuestion {
  const replies = Array.isArray(row.replies) ? row.replies : [];
  const participantIds = new Set<string>([row.created_by].filter(Boolean));
  replies.forEach((reply: any) => {
    if (reply.author_id) participantIds.add(reply.author_id);
  });

  return {
    ...row,
    content_format: row.content_format ?? 'plain',
    author: Array.isArray(row.author) ? row.author[0] : row.author,
    related_faq: Array.isArray(row.related_faq) ? row.related_faq[0] : row.related_faq,
    reply_count: replies.length,
    admin_reply_count: replies.filter((reply: any) => reply.is_admin_reply).length,
    participant_count: participantIds.size,
  } as FaqQuestion;
}

function normalizeReply(row: any, links: FaqReplyFaqLink[] = []): FaqQuestionReply {
  return {
    ...row,
    body_format: row.body_format ?? 'plain',
    author: Array.isArray(row.author) ? row.author[0] : row.author,
    links,
  } as FaqQuestionReply;
}

function requireSpaceId(get: () => FaqQuestionState) {
  const spaceId = get().space?.id;
  if (!spaceId) throw new Error('FAQ 空间尚未初始化');
  return spaceId;
}

export const useFaqQuestionStore = create<FaqQuestionState>((set, get) => ({
  space: null,
  questions: [],
  currentQuestion: null,
  loading: false,
  detailLoading: false,
  saving: false,
  error: null,

  setSpace: (space) => {
    set((state) => {
      if (state.space?.id === space?.id) return { space };
      return {
        space,
        questions: [],
        currentQuestion: null,
        loading: false,
        detailLoading: false,
        saving: false,
        error: null,
      };
    });
  },

  fetchQuestions: async () => {
    set({ loading: true, error: null });
    try {
      const spaceId = requireSpaceId(get);
      const { data, error } = await supabase
        .from('faq_questions')
        .select(questionSelect)
        .eq('space_id', spaceId)
        .order('last_reply_at', { ascending: false, nullsFirst: false })
        .order('created_at', { ascending: false });

      if (error) throw error;
      set({ questions: (data ?? []).map(normalizeQuestion), loading: false });
    } catch (e: any) {
      set({ error: e.message, loading: false });
    }
  },

  fetchQuestionDetail: async (id) => {
    set({ detailLoading: true, error: null });
    try {
      const spaceId = requireSpaceId(get);
      const { data: questionRow, error: questionError } = await supabase
        .from('faq_questions')
        .select(questionSelect)
        .eq('id', id)
        .eq('space_id', spaceId)
        .single();

      if (questionError) throw questionError;

      const { data: replyRows, error: replyError } = await supabase
        .from('faq_question_replies')
        .select(replySelect)
        .eq('question_id', id)
        .order('created_at', { ascending: true });

      if (replyError) throw replyError;

      const replyIds = (replyRows ?? []).map((reply) => reply.id);
      let links: FaqReplyFaqLink[] = [];
      if (replyIds.length > 0) {
        const { data: linkRows, error: linkError } = await supabase
          .from('faq_reply_faq_links')
          .select(`
            *,
            faq:faq_items!faq_reply_faq_links_faq_id_fkey(id,space_id,question,primary_tags,secondary_tags,primary_tag_ids,secondary_tag_ids,is_published,visibility_scope)
          `)
          .in('reply_id', replyIds);

        if (linkError) throw linkError;
        links = (linkRows ?? []).map((link: any) => ({
          ...link,
          faq: Array.isArray(link.faq) ? link.faq[0] : link.faq,
        })) as FaqReplyFaqLink[];
      }

      const linksByReply = new Map<string, FaqReplyFaqLink[]>();
      for (const link of links) {
        const current = linksByReply.get(link.reply_id) ?? [];
        current.push(link);
        linksByReply.set(link.reply_id, current);
      }

      const detail: FaqQuestionDetail = {
        ...normalizeQuestion(questionRow),
        replies: (replyRows ?? []).map((reply) => normalizeReply(reply, linksByReply.get(reply.id) ?? [])),
      };

      set({ currentQuestion: detail, detailLoading: false });
      return detail;
    } catch (e: any) {
      set({ error: e.message, currentQuestion: null, detailLoading: false });
      return null;
    }
  },

  createQuestion: async ({ title, content, contentFormat = 'plain', relatedFaqId }) => {
    set({ saving: true, error: null });
    try {
      const spaceId = requireSpaceId(get);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('请先登录后再提交问题');

      const { data, error } = await supabase
        .from('faq_questions')
        .insert({
          space_id: spaceId,
          title: title.trim(),
          content: content.trim(),
          content_format: contentFormat,
          related_faq_id: relatedFaqId || null,
          created_by: user.id,
        })
        .select(questionSelect)
        .single();

      if (error) throw error;
      const question = normalizeQuestion(data);
      set((s) => ({ questions: [question, ...s.questions], saving: false }));
      return question;
    } catch (e: any) {
      set({ error: e.message, saving: false });
      throw e;
    }
  },

  updateQuestion: async (id, { title, content, contentFormat = 'plain' }) => {
    set({ saving: true, error: null });
    try {
      const spaceId = requireSpaceId(get);
      const { data, error } = await supabase
        .from('faq_questions')
        .update({
          title: title.trim(),
          content: content.trim(),
          content_format: contentFormat,
        })
        .eq('id', id)
        .eq('space_id', spaceId)
        .select(questionSelect)
        .single();

      if (error) throw error;
      const question = normalizeQuestion(data);
      set((s) => ({
        questions: s.questions.map((item) => item.id === id ? question : item),
        currentQuestion: s.currentQuestion?.id === id
          ? { ...s.currentQuestion, ...question }
          : s.currentQuestion,
        saving: false,
      }));
      return question;
    } catch (e: any) {
      set({ error: e.message, saving: false });
      throw e;
    }
  },

  addReply: async ({ questionId, body, bodyFormat = 'plain', isAdminReply = false, linkedFaqIds = [] }) => {
    set({ saving: true, error: null });
    try {
      const spaceId = requireSpaceId(get);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('请先登录');

      const { error: questionError } = await supabase
        .from('faq_questions')
        .select('id')
        .eq('id', questionId)
        .eq('space_id', spaceId)
        .single();

      if (questionError) throw questionError;

      const { data, error } = await supabase
        .from('faq_question_replies')
        .insert({
          question_id: questionId,
          author_id: user.id,
          body: body.trim(),
          body_format: bodyFormat,
          is_admin_reply: isAdminReply,
          email_status: isAdminReply ? 'pending' : 'not_required',
        })
        .select(replySelect)
        .single();

      if (error) throw error;
      const reply = normalizeReply(data);

      if (isAdminReply && linkedFaqIds.length > 0) {
        const rows = Array.from(new Set(linkedFaqIds)).map((faqId) => ({
          reply_id: reply.id,
          faq_id: faqId,
          linked_by: user.id,
        }));
        const { error: linkError } = await supabase
          .from('faq_reply_faq_links')
          .insert(rows);
        if (linkError) throw linkError;
      }

      await get().fetchQuestionDetail(questionId);
      await get().fetchQuestions();
      set({ saving: false });
      return reply;
    } catch (e: any) {
      set({ error: e.message, saving: false });
      throw e;
    }
  },

  updateReply: async (id, { questionId, body, bodyFormat = 'plain' }) => {
    set({ saving: true, error: null });
    try {
      const spaceId = requireSpaceId(get);
      const { error: questionError } = await supabase
        .from('faq_questions')
        .select('id')
        .eq('id', questionId)
        .eq('space_id', spaceId)
        .single();

      if (questionError) throw questionError;

      const { data, error } = await supabase
        .from('faq_question_replies')
        .update({
          body: body.trim(),
          body_format: bodyFormat,
        })
        .eq('id', id)
        .select(replySelect)
        .single();

      if (error) throw error;
      const reply = normalizeReply(data);
      await get().fetchQuestionDetail(questionId);
      await get().fetchQuestions();
      set({ saving: false });
      return reply;
    } catch (e: any) {
      set({ error: e.message, saving: false });
      throw e;
    }
  },

  updateQuestionStatus: async (id, status) => {
    set({ saving: true, error: null });
    try {
      const spaceId = requireSpaceId(get);
      const now = new Date().toISOString();
      const updates: Record<string, string | null> = {
        status,
        closed_at: status === 'closed' ? now : null,
      };
      const { error } = await supabase
        .from('faq_questions')
        .update(updates)
        .eq('id', id)
        .eq('space_id', spaceId);

      if (error) throw error;
      await get().fetchQuestionDetail(id);
      await get().fetchQuestions();
      set({ saving: false });
    } catch (e: any) {
      set({ error: e.message, saving: false });
      throw e;
    }
  },

  sendReplyEmail: async (replyId) => {
    const { error } = await supabase.functions.invoke('faq-send-reply-email', {
      body: {
        reply_id: replyId,
        faq_space_slug: get().space?.slug,
      },
    });
    if (error) throw error;

    const current = get().currentQuestion;
    if (current) {
      await get().fetchQuestionDetail(current.id);
      await get().fetchQuestions();
    }
  },

  clearCurrentQuestion: () => set({ currentQuestion: null, error: null }),
}));
